import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { useDropzone } from 'react-dropzone';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { FileNumberDisclaimer } from './file-number-disclaimer';
import { FilesTable } from './files-table';
import { UploadBreakdownModal } from './upload-breakdown-modal';
import { UploadStatusList } from './upload-status-indicator';
import { FileUploadInput } from './uploader';
import { useFileUploadLogic } from './useFileUploadLogic';
import { useUploadGuards } from './useUploadGuards';
import { useUppyUpload } from './useUppyUpload';
import { OrgPermission } from '../../../../../backend/src/organisation/organisation-permission';
import { TrackingEventsV2 } from '../../../analytics/eventsV2';
import { trackCustomEvent } from '../../../analytics/Mixpanel';
import { APIBaseChronos } from '../../../api/hosts';
import { cn } from '../../lib/utils';
import { Icon } from '../icon';
import { Button } from '../shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../shadcn/dialog';
import { Skeleton } from '../shadcn/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '../shadcn/tooltip';
import { useEmailAttachmentWarnings } from '@/api/hooks/useEmailAttachmentWarnings';
import { useSendPendingFailures } from '@/api/mutations/useSendPendingFailures';
import { useGetFiles } from '@/api/queries/useGetFiles';
import { useGetMatterUploadHistory } from '@/api/queries/useGetMatterUploadHistory';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useUserContext } from '@/Contexts/User';
export function FileUploader({
  setFilesProcessing,
  setFilesExist,
  isMatterCreator = false,
}: {
  setFilesProcessing?: (processing: boolean) => void;
  setFilesExist?: (v: boolean) => void;
  isMatterCreator?: boolean;
}) {
  const { matterId } = useParams();
  if (!matterId) return null;

  // Fetch matter data to get allDocumentsCaseId
  const { data: matter } = useGetMatterV2(matterId);
  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? null;

  // Use a ref to avoid recreating callbacks when allDocumentsCaseId changes
  const allDocumentsCaseIdRef = useRef<string | null>(null);
  allDocumentsCaseIdRef.current = allDocumentsCaseId;

  const queryClient = useQueryClient();

  // TODO: Adjust this based on how many files are currently in the matter
  const maxFiles = 200_000;

  const authInfo = useAuthInfo();
  const { user } = useUserContext();

  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const { mutateAsync: sendPendingFailuresMutation } = useSendPendingFailures({ matterId });
  const { data: files, refetch: refetchFiles, isLoading: isLoadingFiles } = useGetFiles(matterId);
  const { data: uploadHistory } = useGetMatterUploadHistory(matterId);

  // Email attachment warnings - show toast notifications for unsupported email attachments
  useEmailAttachmentWarnings({
    caseId: allDocumentsCaseId ?? '',
    enabled: !!allDocumentsCaseId,
  });

  // --- Uppy upload hook ---
  const {
    onUploadUppy,
    isUploading,
    setIsUploading,
    hasStartedUpload,
    setHasStartedUpload,
    uploadSessions,
    uppyProgressByUpload,
    handleProcessingComplete,
    uploadSessionsRef,
  } = useUppyUpload({
    matterId,
    allDocumentsCaseIdRef,
    sendPendingFailures: sendPendingFailuresMutation,
    refetchFiles,
    queryClient,
    maxFiles,
  });

  // --- Upload guards (beforeunload + navigation blocking) ---
  const { pendingNavigation, setPendingNavigation, originalNavigatorRef } = useUploadGuards({
    isUploading,
    uploadSessionsRef,
    matterId,
  });

  const onApideckSuccess = useCallback(() => {
    refetchFiles();
  }, [refetchFiles]);

  const isApideckEnabled = user?.organisation?.permissions.includes(OrgPermission.Apideck);

  const {
    handleFileAccepted,
    modalOpen,
    setModalOpen,
    modalContent,
    isRelativityModalOpen,
    setIsRelativityModalOpen,
    apiDeckSessionToken,
    apiDeckConnections,
    connectRelativity,
    handleOpenApideckModal,
    handleSelectRelativityList,
  } = useFileUploadLogic({
    subsetId: allDocumentsCaseId,
    isApideckEnabled,
    maxFiles,
    onApideckSuccess,
  });

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    multiple: true,
    noClick: true,
    onDropAccepted: (acceptedFiles) => {
      handleFileAccepted(acceptedFiles, onUploadUppy);
    },
    maxSize: 3 * 1024 * 1024 * 1024, // 3GB
    maxFiles,
  });

  // Uploads currently being processed by the backend
  const processingUploads = useMemo(
    () => (uploadHistory ?? []).filter((u) => u.status === 'processing'),
    [uploadHistory],
  );

  const isProcessing = processingUploads.length > 0;

  // Used to pass up to parent state the fact that an upload is ongoing
  useEffect(() => {
    setFilesProcessing?.(isUploading || isProcessing);
  }, [isUploading, isProcessing]);

  useEffect(() => {
    if (files) {
      setFilesExist?.(files.length > 0);
    }
  }, [files, setFilesExist]);

  // Expand files section when there are processing uploads discovered from DB
  useEffect(() => {
    if (processingUploads.length > 0) {
      setHasStartedUpload(true);
    }
  }, [processingUploads]);

  // Expand files section once any upload starts or files already exist
  const hasFiles = hasStartedUpload || (files !== undefined && files.length > 0);

  return (
    <div className="relative flex h-full overflow-y-hidden focus-visible:outline-none" {...getRootProps()}>
      {/* Dashed border */}
      <div
        className={cn(
          isDragActive ? 'opacity-100' : 'opacity-0',
          'pointer-events-none absolute inset-0 z-20 bg-black/6 duration-150',
        )}
      />
      {/* Upload Section */}
      <div
        className={cn(
          hasFiles ? 'w-1/3' : 'w-1/2',
          isDragActive ? 'pointer-events-none bg-gray-200/60' : 'bg-gray-100',
          'relative flex min-w-1/3 items-center justify-center border-r px-6 duration-150',
        )}
      >
        <FileUploadInput
          maxFiles={maxFiles}
          matterId={matterId}
          subsetId={allDocumentsCaseId}
          onApideckSuccess={onApideckSuccess}
          isApideckEnabled={isApideckEnabled}
          // Dropzone props
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          open={open}
          // useFileUploadLogic props
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          modalContent={modalContent}
          isRelativityModalOpen={isRelativityModalOpen}
          setIsRelativityModalOpen={setIsRelativityModalOpen}
          apiDeckSessionToken={apiDeckSessionToken}
          apiDeckConnections={apiDeckConnections}
          connectRelativity={connectRelativity}
          handleOpenApideckModal={handleOpenApideckModal}
          handleSelectRelativityList={handleSelectRelativityList}
        />

        <FileNumberDisclaimer />
      </div>

      {/* Files Section */}
      <div className={cn('flex min-w-sm grow flex-col px-6 py-4 pr-4 pb-1', isDragActive && 'pointer-events-none')}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <h2 className="font-serif text-2xl font-medium">Files</h2>
            <Tooltip>
              <TooltipTrigger className="-mb-0.5 pl-1 text-gray-400">
                <Icon className="lucide--info" />
              </TooltipTrigger>
              <TooltipContent side="right">
                These files have been uploaded, but not yet processed by Wexler.
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-3">
            {processingUploads.length > 0 && (
              <span className="text-xs text-gray-500">
                {processingUploads.length} upload{processingUploads.length !== 1 ? 's' : ''} processing
                {files && files.length > 0
                  ? ` · ${files.length.toLocaleString()} file${files.length !== 1 ? 's' : ''} awaiting processing`
                  : ''}
              </span>
            )}
            {processingUploads.length === 0 && files && files.length > 0 && (
              <span className="text-xs text-gray-500">
                {files.length.toLocaleString()} file{files.length !== 1 ? 's' : ''} awaiting processing
              </span>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                if (isMatterCreator) {
                  setFlowModalOpen(true);
                  trackCustomEvent(TrackingEventsV2.FileHistoryOpenBreakdownModal, { matterId });
                } else {
                  trackCustomEvent(TrackingEventsV2.FileHistoryOpenBreakdownLink, { matterId });
                  window.open(`/matters/${matterId}/settings/file-history`, '_blank');
                }
              }}
            >
              <Icon className="lucide--git-branch" />
              View Upload Breakdown
            </Button>
          </div>
        </div>

        <UploadStatusList
          uploadSessions={uploadSessions}
          uploadHistory={uploadHistory}
          uppyProgressByUpload={uppyProgressByUpload}
          matterId={matterId}
          onProcessingComplete={handleProcessingComplete}
        />

        {hasFiles ? (
          <FilesTable files={files ?? []} isLoading={isLoadingFiles} />
        ) : isLoadingFiles ? (
          <Skeleton className="mt-2 h-4 w-16" />
        ) : (
          <p className="mt-2 text-gray-500">No files added yet</p>
        )}
      </div>

      <UploadBreakdownModal open={flowModalOpen} onOpenChange={setFlowModalOpen} matterId={matterId} />

      {/* Confirmation dialog when navigating away during upload */}
      <Dialog
        open={pendingNavigation !== null}
        onOpenChange={(open) => {
          if (!open) setPendingNavigation(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload in progress</DialogTitle>
            <DialogDescription>
              Files are still being uploaded to storage. If you leave now, only files that have already been uploaded
              will be processed.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Once uploading finishes, processing (deduplication, extraction, etc.) will continue in the background even
            if you navigate away.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="default" onClick={() => setPendingNavigation(null)}>
              Stay
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                // Signal done for all sessions — already-submitted batches continue
                // processing and remain visible as "processing" in upload history.
                void (async () => {
                  const token = await authInfo.tokens.getAccessToken();
                  for (const session of uploadSessionsRef.current) {
                    await fetch(`${APIBaseChronos}/api/matter/${matterId}/upload-metadata/${session.uploadId}/done`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    }).catch(() => {});
                  }
                  setIsUploading(false);

                  if (pendingNavigation && originalNavigatorRef.current) {
                    const { action, args } = pendingNavigation;
                    const orig = originalNavigatorRef.current;
                    setPendingNavigation(null);
                    if (action === 'push') {
                      (orig.push as Function)(...args);
                    } else {
                      (orig.replace as Function)(...args);
                    }
                  }
                })();
              }}
            >
              Leave
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
